// src/components/UserProfile.jsx
import React, { useState, useEffect, useRef } from "react";
import { Auth, Storage } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { Button, Row, Col, Spinner, Tabs, Tab } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPaw,
  faBowlFood,
  faMicrochip,
  faLock,
} from "@fortawesome/free-solid-svg-icons";
import useScrollToTop from "../helpers/useScrollToTop";
import { useSubscriptionStatus } from "../lib/useSubscriptionStatus";

import "./UserProfile.css";

// 🔗 Match the same API base you use in FeedingSchedulePage
const API_BASE = process.env.REACT_APP_API_BASE || "";

// 🔗 Match the same Lambda URL you used in SmartTagSetup
const SAVE_PET_LAMBDA_URL =
  "https://pax3jhmxsuub7f7fdnm3xa3gta0bbtok.lambda-url.us-east-1.on.aws/";

function UserProfile() {
  useScrollToTop();
  const navigate = useNavigate();
  const { status: subStatus, loading: subLoading } = useSubscriptionStatus();
  const isSubActive = subStatus === "active";

  const [activeTab, setActiveTab] = useState("feeding");

  const [userAttrs, setUserAttrs] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  const [petProfile, setPetProfile] = useState(null);
  const [loadingPet, setLoadingPet] = useState(false);
  const [petError, setPetError] = useState(null);

  // 🖼 Pet photo state
  const [petPhotoUrl, setPetPhotoUrl] = useState(
    () => localStorage.getItem("lpp.petPhotoUrl") || null
  );

  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState("");
  const photoInputRef = useRef(null);

  // Feeding summary state (single source of truth)
  const [feedingPlan, setFeedingPlan] = useState(null);
  const [feedingLoading, setFeedingLoading] = useState(false);
  const [feedingError, setFeedingError] = useState("");

  const storedTagId = (localStorage.getItem("lpp.tagId") || "").toUpperCase();
  const tabsRef = useRef(null);


  const goSubscribe = () => navigate("/subscribe");

  const handleAskPawNutritionAndTraining = () => {
    if (!isSubActive) return goSubscribe();

    navigate("/pawchat", {
      state: {
        topic: "nutrition_and_training",
        prefill:
          "I want help with my pet's nutrition and training. Can you give me a plan or tips?",
      },
    });
  };

  function getFeedingRiskLabel(flag) {
    if (flag === "overfed") return "May be overfed based on calories.";
    if (flag === "underfed") return "May be underfed based on calories.";
    if (flag === "balanced") return "Looks balanced for size & activity.";
    return "Add weight and calories to see guidance.";
  }

  function getFoodReminderSummary(plan) {
    const raw = plan?.meta?.foodReminderDate; // "YYYY-MM-DD"
    if (!raw) return "Not set yet.";

    const [y, m, d] = String(raw).split("-");
    if (!y || !m || !d) return "Not set yet.";

    return `One-time text check-in around ${m}/${d}/${y}.`;
  }
useEffect(() => {
  if (!tabsRef.current) return;

  // small delay so the tab content has actually rendered
  requestAnimationFrame(() => {
    tabsRef.current.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "nearest",
    });
  });
}, [activeTab]);

  // 🔐 Load user attributes
  useEffect(() => {
    (async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        setUserAttrs(user.attributes || {});
      } catch (err) {
        console.error("Error fetching user details", err);
      } finally {
        setLoadingUser(false);
      }
    })();
  }, []);

  // 🐾 Fetch pet profile for this user's tag
  const fetchPetProfile = async (tagId) => {
    const TAG = (tagId || "").toUpperCase();

    try {
      setLoadingPet(true);
      setPetError(null);

      const resp = await fetch(SAVE_PET_LAMBDA_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "get", tagId: TAG }),
      });

      if (!resp.ok) {
        throw new Error(`Profile fetch failed: ${resp.status}`);
      }

      const data = await resp.json();

      if (data && data.petName) {
        const profile = {
          tagId: (data.tagId || TAG).toUpperCase(),
          name: data.petName,
          species: data.species,
          breed: data.breed,
          age: data.age,
        };

        setPetProfile(profile);

        // ✅ keep local storage in sync with THIS tag only
        localStorage.setItem("lpp.tagId", profile.tagId);
        localStorage.setItem("lpp.petName", profile.name || "");
      } else {
        // ✅ IMPORTANT: clear stale cached pet name for new accounts/tags
        setPetProfile(null);
        localStorage.setItem("lpp.tagId", TAG);
        localStorage.removeItem("lpp.petName");
      }
    } catch (err) {
      console.error("Error fetching pet profile:", err);
      setPetError("We couldn't load your pet profile right now.");

      // ✅ also clear stale cached pet name on failure
      setPetProfile(null);
      if (TAG) localStorage.setItem("lpp.tagId", TAG);
      localStorage.removeItem("lpp.petName");
    } finally {
      setLoadingPet(false);
    }
  };

  // 🖼 Helper: load the latest pet photo from S3 (public)
  const loadPetPhoto = async (tagId) => {
    const TAG = (tagId || "").toUpperCase();
    if (!TAG) return;

    try {
      setPhotoError("");

      // ✅ always clear current photo when switching tags
      setPetPhotoUrl(null);
      localStorage.removeItem("lpp.petPhotoUrl");

      const listResult = await Storage.list(`pets/${TAG}/`, { level: "public" });
      const items = listResult?.results || listResult || [];

      // ✅ if no photos, keep it null (don’t fall back to previous)
      if (!items.length) return;

      const sorted = [...items].sort((a, b) => {
        const aTime = new Date(a.lastModified || 0).getTime();
        const bTime = new Date(b.lastModified || 0).getTime();
        return bTime - aTime;
      });

      const latest = sorted[0];
      const url = await Storage.get(latest.key, { level: "public" });

      setPetPhotoUrl(url);
      localStorage.setItem("lpp.petPhotoUrl", url);
    } catch (err) {
      console.error("Error loading pet photo:", err);
      setPhotoError("We couldn't load your pet photo just yet.");
      setPetPhotoUrl(null);
      localStorage.removeItem("lpp.petPhotoUrl");
    }
  };

  const loadFeedingPlan = async (tagId) => {
    if (!API_BASE || !tagId) return;

    try {
      setFeedingLoading(true);
      setFeedingError("");

      const resp = await fetch(`${API_BASE}/pets/${tagId}/feeding`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`Feeding GET failed: ${resp.status} ${text}`);
      }

      const data = await resp.json();
      setFeedingPlan(data.feedingPlan || null);
    } catch (err) {
      console.error("Feeding plan fetch failed:", err);
      setFeedingPlan(null);
      setFeedingError(
        "We couldn’t load your feeding plan yet. You can still manage it on the Feeding & Food page."
      );
    } finally {
      setFeedingLoading(false);
    }
  };

  useEffect(() => {
    if (!userAttrs) return;

    const preferredTag =
      (userAttrs.preferred_username || storedTagId || "").toUpperCase();

    if (!preferredTag) return;

    fetchPetProfile(preferredTag);
    loadPetPhoto(preferredTag);
    loadFeedingPlan(preferredTag);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userAttrs]);

  const handlePhotoSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoError("");

    if (!userAttrs) return;

    const preferredTag =
      (userAttrs.preferred_username || storedTagId || "").toUpperCase();
    if (!preferredTag) {
      setPhotoError("You need an active SmartTag to add photos.");
      return;
    }

    try {
      setUploadingPhoto(true);

      const ext = file.name.split(".").pop() || "jpg";
      const key = `pets/${preferredTag}/profile-${Date.now()}.${ext}`;

      await Storage.put(key, file, {
        level: "public",
        contentType: file.type,
      });

      const url = await Storage.get(key, { level: "public" });

      // ✅ show in profile
      setPetPhotoUrl(url);

      // ✅ make it available to all headers
      localStorage.setItem("lpp.petPhotoUrl", url);
      localStorage.setItem("lpp.tagId", preferredTag);
      if (petProfile?.name) {
        localStorage.setItem("lpp.petName", petProfile.name);
      }
    } catch (err) {
      console.error("Upload error:", err);
      setPhotoError("We couldn't upload that photo. Please try again.");
    } finally {
      setUploadingPhoto(false);
      if (photoInputRef.current) {
        photoInputRef.current.value = "";
      }
    }
  };

  if (loadingUser) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  if (!userAttrs) {
    return (
      <div className="text-center py-5">
        <p>We couldn’t load your profile details.</p>
        <Button variant="outline-primary" onClick={() => navigate("/")}>
          Go Home
        </Button>
      </div>
    );
  }

  const preferredTag =
    (userAttrs.preferred_username || storedTagId || "").toUpperCase();

  return (
    <div className="user-profile-wrapper">
      <div className="profile-shell">
        <main className="profile-body">
          <Row className="gy-4">
            <Col md={12}>
              <section className="profile-section-panel">
                <h4 className="mb-2 lpp-section-title">Your Pet & SmartTag</h4>

                <p className="lpp-section-subtitle small mb-3">
                  Linked pet profile, photo, and your key tools in one place.
                </p>

                {/* Pet photo */}
                {preferredTag && (
                  <div className="d-flex align-items-center mb-3">
                    <div className="me-3">
                      {petPhotoUrl ? (
                        <img
                          src={petPhotoUrl}
                          alt={petProfile?.name || "Pet photo"}
                          className="profile-pet-photo"
                        />
                      ) : (
                        <div className="profile-pet-photo placeholder">🐾</div>
                      )}
                    </div>

                    <div className="flex-grow-1">
                      <Button
                        size="sm"
                        className="lpp-pill-btn-outline"
                        onClick={() => photoInputRef.current?.click()}
                        disabled={uploadingPhoto}
                      >
                        {uploadingPhoto
                          ? "Uploading..."
                          : petPhotoUrl
                          ? "Change photo"
                          : "Add a photo"}
                      </Button>

                      <input
                        ref={photoInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={handlePhotoSelected}
                      />

                      {photoError && (
                        <div className="small text-danger mt-1">{photoError}</div>
                      )}

                      {!photoError && (
                        <div className="small lpp-label mt-1">
                          A clear face photo helps if your pet ever gets lost.
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {!preferredTag && (
                  <p className="lpp-label small mb-0">
                    You don’t have a SmartTag linked yet. Once you activate a
                    tag, you’ll see your pet’s details here.
                  </p>
                )}

                {preferredTag && (
                  <>
                    {loadingPet ? (
                      <div className="d-flex align-items-center mb-2">
                        <Spinner animation="border" size="sm" className="me-2" />
                        <span className="small lpp-label">Loading pet profile…</span>
                      </div>
                    ) : petError ? (
                      <p className="text-danger small mb-2">{petError}</p>
                    ) : petProfile ? (
                      <Row className="mb-3 align-items-center text-center text-md-start">
                        <div
                          className="d-flex flex-wrap align-items-center py-2"
                          style={{ gap: "1rem", fontSize: "0.95rem" }}
                        >
                          <div className="d-flex align-items-center">
                            <small className="lpp-label me-2">Pet Name</small>
                            <span className="profile-field-value">
                              {petProfile.name || "Unnamed Pet"}
                            </span>
                          </div>

                          <div className="vr d-none d-md-block profile-vr" />

                          <div className="d-flex align-items-center">
                            <small className="lpp-label me-2">Species / Breed</small>
                            <span className="profile-field-value">
                              {petProfile.species || "Unknown"} •{" "}
                              {petProfile.breed || "Unknown"}
                            </span>
                          </div>

                          <div className="vr d-none d-md-block profile-vr" />

                          <div className="d-flex align-items-center">
                            <small className="lpp-label me-2">Age</small>
                            <span className="profile-field-value">
                              {petProfile.age || "Unknown"}
                            </span>
                          </div>

                          <div className="vr d-none d-md-block profile-vr" />

                          <div className="d-flex align-items-center">
                            <small className="lpp-label me-2">SmartTag</small>
                            <span className="profile-field-value">
                              <span className="badge lpp-badge-tag">
                                {petProfile.tagId}
                              </span>
                            </span>
                          </div>
                        </div>
                      </Row>
                    ) : (
                      <p className="lpp-label small mb-3">
                        SmartTag {preferredTag} is linked, but we don’t have a pet
                        profile yet. Use <strong>Manage SmartTag</strong> with Paw
                        to set it up.
                      </p>
                    )}

                    <hr className="profile-divider" />

                    {/* ✅ 4-tab dashboard */}
                 <div className="lpp-tabs-shell mt-3" ref={tabsRef}>
                  <Tabs
                    activeKey={activeTab}
                    onSelect={(k) => setActiveTab(k)}
                    className="lpp-tabs"
                    justify
                  >
                        {/* 1) Feeding */}
                        <Tab
                          eventKey="feeding"
                          title={
                            <span className="lpp-tab-title">
                              <FontAwesomeIcon icon={faBowlFood} className="me-2" />
                              Feeding
                            </span>
                          }
                        >
                          <div className="lpp-tab-panel">
                            <h6 className="lpp-tab-heading">Feeding plan</h6>
                            <p className="small text-muted mb-2">
                              Your saved plan + food check-in reminders.
                            </p>

                            {feedingLoading && (
                              <p className="small text-muted mb-2">
                                Loading feeding plan…
                              </p>
                            )}

                            {!feedingLoading && feedingError && (
                              <p className="small text-danger mb-2">{feedingError}</p>
                            )}

                            {!feedingLoading && !feedingPlan && !feedingError && (
                              <p className="small text-muted mb-2">
                                No feeding plan saved yet.
                              </p>
                            )}

                            {!feedingLoading && feedingPlan && !feedingError && (
                              <div className="small">
                                <div className="mb-1">
                                  <strong>Food check-in:</strong>{" "}
                                  {getFoodReminderSummary(feedingPlan)}
                                </div>

                                <div className="mb-1">
                                  <strong>Food:</strong>{" "}
                                  {feedingPlan.foodProfile?.brand ||
                                  feedingPlan.foodProfile?.productName ? (
                                    <>
                                      {feedingPlan.foodProfile.brand}
                                      {feedingPlan.foodProfile.brand &&
                                      feedingPlan.foodProfile.productName
                                        ? " – "
                                        : ""}
                                      {feedingPlan.foodProfile.productName}
                                    </>
                                  ) : (
                                    "Not set."
                                  )}
                                </div>

                                <div className="mb-1">
                                  <strong>Daily amount:</strong>{" "}
                                  {feedingPlan.schedule?.totalDailyCups != null
                                    ? `${Number(
                                        feedingPlan.schedule.totalDailyCups
                                      ).toFixed(2)} cups/day`
                                    : "Not set."}
                                </div>

                                <div className="text-muted mt-2">
                                  {getFeedingRiskLabel(
                                    feedingPlan.calorieProfile?.riskFlag
                                  )}
                                </div>
                              </div>
                            )}

                            <div className="d-flex justify-content-end mt-3">
                              <Button
                                size="sm"
                                className="lpp-pill-btn d-inline-flex align-items-center gap-2"
                                onClick={() => navigate("/feeding")}
                              >
                                <FontAwesomeIcon icon={faBowlFood} />
                                Open Feeding & Food
                              </Button>
                            </div>
                          </div>
                        </Tab>

                        {/* 2) Ask Paw */}
                        <Tab
                          eventKey="paw"
                          title={
                            <span className="lpp-tab-title">
                              <FontAwesomeIcon icon={faPaw} className="me-2" />
                              Ask Paw
                            </span>
                          }
                        >
                          <div className="lpp-tab-panel">
                            <h6 className="lpp-tab-heading">Ask Paw</h6>
                            <p className="small text-muted mb-2">
                              Nutrition help, training tips, and quick answers—based
                              on your pet.
                            </p>

                            {!subLoading && !isSubActive && (
                              <div className="small text-muted mb-2">
                                Subscribe to unlock Paw.
                              </div>
                            )}

                            <div className="d-flex justify-content-end mt-3">
                              <Button
                                size="sm"
                                className="lpp-pill-btn d-inline-flex align-items-center gap-2"
                                onClick={handleAskPawNutritionAndTraining}
                                disabled={subLoading}
                              >
                                <FontAwesomeIcon icon={isSubActive ? faPaw : faLock} />
                                Open Paw Chat
                              </Button>
                            </div>
                          </div>
                        </Tab>

                        {/* 3) Notes */}
                        <Tab
                          eventKey="notes"
                          title={
                            <span className="lpp-tab-title">
                              <FontAwesomeIcon icon={faPaw} className="me-2" />
                              Notes
                            </span>
                          }
                        >
                          <div className="lpp-tab-panel">
                            <h6 className="lpp-tab-heading">Notes</h6>
                            <p className="small text-muted mb-2">
                              Track behavior, training progress, and important
                              moments.
                            </p>

                            {!subLoading && !isSubActive && (
                              <div className="small text-muted mb-2">
                                Subscribe to unlock Notes.
                              </div>
                            )}

                            <div className="d-flex justify-content-end mt-3">
                              <Button
                                size="sm"
                                className="lpp-pill-btn d-inline-flex align-items-center gap-2"
                                onClick={() =>
                                  isSubActive ? navigate("/notes") : goSubscribe()
                                }
                                disabled={subLoading}
                              >
                                <FontAwesomeIcon icon={isSubActive ? faPaw : faLock} />
                                Open Notes
                              </Button>
                            </div>
                          </div>
                        </Tab>

                        {/* 4) Health */}
                        <Tab
                          eventKey="health"
                          title={
                            <span className="lpp-tab-title">
                              <FontAwesomeIcon icon={faMicrochip} className="me-2" />
                              Health
                            </span>
                          }
                        >
                          <div className="lpp-tab-panel">
                            <h6 className="lpp-tab-heading">Health</h6>
                            <p className="small text-muted mb-2">
                              Vet reminders and document history tied to your SmartTag.
                            </p>
                             {!subLoading && !isSubActive && (
                              <div className="small text-muted mb-2">
                                Subscribe to unlock Notes.
                              </div>
                            )}

                            <div className="d-flex justify-content-end mt-3">
                              <Button
                                size="sm"
                                className="lpp-pill-btn d-inline-flex align-items-center gap-2"
                                onClick={() =>
                                  isSubActive ? navigate("/health") : goSubscribe()
                                }
                                disabled={subLoading}
                              >
                                <FontAwesomeIcon icon={isSubActive ? faMicrochip : faLock} />

                                Open Health Timeline
                              </Button>
                            </div>
                          </div>
                        </Tab>
                      </Tabs>
                    </div>
                  </>
                )}
              </section>
            </Col>
          </Row>
        </main>
      </div>
    </div>
  );
}

export default UserProfile;
